import {
  ProjectAudienceSettings,
  ProjectBasicSettings,
  ProjectContentSettings,
  ProjectEmailSettings,
  ProjectInsightsSettings,
  ProjectMessagingSettings,
  ProjectSmsSettings,
  ProjectTrackingSettings,
  UpdateProject,
} from 'modules/domains';
import {
  AUDIENCE_FIELD_NAMES,
  CONTENT_FIELD_NAMES,
  EMAIL_FIELD_NAMES,
  INSIGHTS_FIELD_NAMES,
  MESSAGING_FIELD_NAMES,
  PROJECT_FIELD_NAMES,
  SMS_FIELD_NAMES,
  SmsOptOutValues,
  TRACKING_FIELD_NAMES,
} from '../constants';
import {
  DirtyFields,
  ProjectSettingsFormSchemaType,
} from '../contexts/ProjectSettingsFormProvider';

export const isSectionDirty = (fieldNames: string[], dirtyFields: DirtyFields) =>
  fieldNames.some((fieldName) => dirtyFields[fieldName as keyof ProjectSettingsFormSchemaType]);

/** Basic settings payload */
export const basicSettingsPayload = (
  values: ProjectSettingsFormSchemaType,
  dirtyFields: DirtyFields,
): ProjectBasicSettings | undefined => {
  const { NAME, TIME_ZONE, PHYSICAL_ADDRESS } = PROJECT_FIELD_NAMES;
  const isDirty = isSectionDirty([NAME, PHYSICAL_ADDRESS, TIME_ZONE], dirtyFields);
  if (isDirty) {
    return {
      name: values.name,
      timeZone: values.timeZone,
      physicalAddress: values.physicalAddress,
    };
  }
};

/** Email settings payload */
export const emailSettingsPayload = (
  values: ProjectSettingsFormSchemaType,
  dirtyFields: DirtyFields,
): ProjectEmailSettings | undefined => {
  const isDirty = isSectionDirty(Object.values(EMAIL_FIELD_NAMES), dirtyFields);
  const customUnsubscribeSnippet = !!values.useDoubleOptOut ? values.customUnsubscribeSnippet : '';
  if (isDirty) {
    return {
      customUnsubscribeSnippet,
      firstNameField: values.firstNameField,
      lastNameField: values.lastNameField,
      ipTracking: {
        useDynamicSenders: values.useDynamicSenders,
        /**
         * These below two fields are in the Audience Section in the UI,
         * but must be included here in the email settings payload.
         */
        enableGeoIPLookup: values.enableGeoIPLookup,
        enableEmailClickIPUpdate: values.enableEmailClickIPUpdate,
      },
      sendingDomain:
        values.ENABLE_SENDING_DOMAIN && Boolean(values.sendingDomain)
          ? values.sendingDomain
          : undefined,
      addUnsubscribe: values.addUnsubscribe,
      useDoubleOptOut: values.useDoubleOptOut,
      hostedUnsubscribeUrl: values.hostedUnsubscribeUrl || undefined,
      useHostedUnsubscribeUrlInListUnsubscribeHeader:
        values.useHostedUnsubscribeUrlInListUnsubscribeHeader,
      subscribeSuccessUrl: values.subscribeSuccessUrl,
    };
  }
};

/** Audience settings payload */
export const audienceSettingsPayload = (
  values: ProjectSettingsFormSchemaType,
  dirtyFields: DirtyFields,
): ProjectAudienceSettings | undefined => {
  const fieldNames = Object.values(AUDIENCE_FIELD_NAMES);
  const isDirty = isSectionDirty(fieldNames, dirtyFields);

  if (isDirty) {
    return {
      enableAnonUserTracking: values.enableAnonUserTracking,
    };
  }
};

/** SMS settings payload */
export const smsSettingsPayload = (
  values: ProjectSettingsFormSchemaType,
  dirtyFields: DirtyFields,
): ProjectSmsSettings | undefined => {
  const isDirty = isSectionDirty(Object.values(SMS_FIELD_NAMES), dirtyFields);
  if (isDirty) {
    return {
      disableSMSDisclaimer: values.SMS_OPT_OUT_INSTRUCTIONS === SmsOptOutValues.Never,
      isSMSDisclaimerOnAllMessages: values.SMS_OPT_OUT_INSTRUCTIONS === SmsOptOutValues.Every,
      smsDisclaimer: values.smsDisclaimer,
      shortLinksSettings: {
        marketingMin: values.smsShortlinksMinLengthMarketing,
        transactionalMin: values.smsShortlinksMinLengthTransactional,
      },
    };
  }
};

/** Content settings payload */
export const contentSettingsPayload = (
  values: ProjectSettingsFormSchemaType,
  dirtyFields: DirtyFields,
): ProjectContentSettings | undefined => {
  const isDirty = isSectionDirty(Object.values(CONTENT_FIELD_NAMES), dirtyFields);

  if (isDirty) {
    return {
      wysiwygEditorMode: values.wysiwygEditorMode,
      HMACSecret: values.HMACSecret,
      locales: values.locales,
      mappedLocales: values.mappedLocales,
      templateDefaultLocaleModifiable: values.templateDefaultLocaleModifiable,
      localeSettings: {
        overrideContentNotFoundMarketing: values.overrideContentNotFoundMarketing,
        overrideContentNotFoundTransactional: values.overrideContentNotFoundTransactional,
      },
    };
  }
};

/** Insights settings payload */
export const insightsSettingsPayload = (
  values: ProjectSettingsFormSchemaType,
  dirtyFields: DirtyFields,
): ProjectInsightsSettings | undefined => {
  const isDirty = isSectionDirty(Object.values(INSIGHTS_FIELD_NAMES), dirtyFields);
  if (isDirty) {
    return {
      showRevenue: values.showRevenue,
      enableImprovedTracking: values.enableImprovedTracking,
    };
  }
};

export const trackingSettingsPayload = (
  values: ProjectSettingsFormSchemaType,
  dirtyFields: DirtyFields,
): ProjectTrackingSettings | undefined => {
  const isDirty = isSectionDirty(Object.values(TRACKING_FIELD_NAMES), dirtyFields);
  if (isDirty) {
    return {
      useFilteredTracking: values.useFilteredTracking,
    };
  }
};

export const messagingSettingsPayload = (
  values: ProjectSettingsFormSchemaType,
  dirtyFields: DirtyFields,
): ProjectMessagingSettings | undefined => {
  const isDirty = isSectionDirty([MESSAGING_FIELD_NAMES.GROUP_RECURRING_CAMPAIGNS], dirtyFields);
  if (isDirty) {
    return {
      groupRecurringCampaigns: values.groupRecurringCampaigns,
    };
  }
};

export const getUpdateProjectSettingsPayload = (
  values: ProjectSettingsFormSchemaType,
  dirtyFields: DirtyFields,
): UpdateProject => {
  return {
    audienceSettings: audienceSettingsPayload(values, dirtyFields),
    basicSettings: basicSettingsPayload(values, dirtyFields),
    contentSettings: contentSettingsPayload(values, dirtyFields),
    emailSettings: emailSettingsPayload(values, dirtyFields),
    insightsSettings: insightsSettingsPayload(values, dirtyFields),
    messagingSettings: messagingSettingsPayload(values, dirtyFields),
    smsSettings: smsSettingsPayload(values, dirtyFields),
    trackingSettings: trackingSettingsPayload(values, dirtyFields),
  };
};
